import { ILayer } from '../layer.type';
import { environment } from '../../../../../environments/environment';
import { getCSSColorVariableValue } from '../../../utils';
import { _ } from '../../../utils';
import { getSharedLayer } from './shared';
import { TenantConfig } from './tenant-config.type';

const SYKE_ATTRIBUTION =
  '<a href="https://www.syke.fi/avointieto" target="_blank">SYKE</a>';

const sharedLayers = [
  getSharedLayer('rails'),
  getSharedLayer('levelcrossings'),
  getSharedLayer('trafficControlPoints'),
  getSharedLayer('intersectionStations'),
  getSharedLayer('contour'),
  getSharedLayer('depthContour'),
  getSharedLayer('groundWaterAreas'),
  getSharedLayer('drainageAreas'),
  getSharedLayer('basinOutlets'),
  getSharedLayer('houseNumbers')
];

/**
 * Joensuu
 */
export default (): TenantConfig => ({
  layerDefinitions: getLayers(),
  hasMultipleMarkerModes: false,
  defaultCenter: {
    lat: 62.598,
    lng: 29.783,
  },
  defaultZoom: 10.96,
});

// vectorURL and vectorSourceLayer are filled in from encrypted-data/T13.json by MapService.addLayer
export function getLayers(): ILayer[] {
  return [
    {
      id: 'JNS_wastewater_network',
      name: _('Wastewater network'),
      orderingWeight: 0,
      selectable: true,
      style: {
        clickable: true,
        strokeColor: '#cf923b',
        strokeWeight: 1.5,
        visible: false,
        layerType: 'line',
        sourceType: 'vector',
      },
      metadata: {
        visibleByZoom: 10,
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: { coordinates: true, properties: ['EntityHandle'] },
        uniqueBy: 'EntityHandle',
        labelBy: 'EntityHandle',
      },
      searchOptions: {
        interestingProperties: [{ key: 'EntityHandle', type: 'contains' }],
        searchable: false,
      },
      useWorker: true,
    },
    {
      id: 'JNS_clean_water_network',
      name: _('Clean water network'),
      orderingWeight: 0,
      selectable: true,
      style: {
        clickable: true,
        strokeColor: getCSSColorVariableValue('info-base'),
        strokeWeight: 1.5,
        visible: false,
        layerType: 'line',
        sourceType: 'vector',
      },
      metadata: {
        visibleByZoom: 10,
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: { coordinates: true, properties: ['EntityHandle'] },
        uniqueBy: 'EntityHandle',
        labelBy: 'EntityHandle',
      },
      searchOptions: {
        interestingProperties: [{ key: 'EntityHandle', type: 'contains' }],
        searchable: false,
      },
      useWorker: true,
    },
    {
      id: 'JNS_subsurface_drain_network',
      name: _('Subsurface drain network'),
      orderingWeight: 0,
      selectable: true,
      style: {
        clickable: true,
        strokeColor: getCSSColorVariableValue('success-base'),
        strokeWeight: 1.5,
        visible: false,
        layerType: 'line',
        sourceType: 'vector',
      },
      metadata: {
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: { coordinates: true, properties: ['EntityHandle'] },
        uniqueBy: 'EntityHandle',
        labelBy: 'EntityHandle',
      },
      searchOptions: {
        interestingProperties: [{ key: 'EntityHandle', type: 'contains' }],
        searchable: false,
      },
      useWorker: true,
    },
    {
      id: 'JNS_rainwater_network',
      name: _('Urban runoff network'),
      orderingWeight: 0,
      selectable: true,
      style: {
        clickable: true,
        strokeColor: getCSSColorVariableValue('success-base'),
        strokeWeight: 1.5,
        visible: false,
        layerType: 'line',
        sourceType: 'vector',
      },
      metadata: {
        visibleByZoom: 10,
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: { coordinates: true, properties: ['EntityHandle'] },
        uniqueBy: 'EntityHandle',
        labelBy: 'EntityHandle',
      },
      searchOptions: {
        interestingProperties: [{ key: 'EntityHandle', type: 'contains' }],
        searchable: false,
      },
      useWorker: true,
    },
    {
      id: 'VOK_clean_water_network',
      name: _('VOK clean water network'),
      orderingWeight: 0,
      selectable: true,
      style: {
        clickable: true,
        strokeColor: getCSSColorVariableValue('success-base'),
        strokeWeight: 1.5,
        visible: false,
        layerType: 'line',
        sourceType: 'vector',
      },
      metadata: {
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: { coordinates: true, properties: ['EntityHandle'] },
        uniqueBy: 'EntityHandle',
        labelBy: 'EntityHandle',
      },
      searchOptions: {
        interestingProperties: [{ key: 'EntityHandle', type: 'contains' }],
        searchable: false,
      },
      useWorker: true,
    },
    {
      id: 'VOK_wastewater_network',
      name: _('VOK wastewater network'),
      orderingWeight: 0,
      selectable: true,
      style: {
        clickable: true,
        strokeColor: getCSSColorVariableValue('success-base'),
        strokeWeight: 1.5,
        visible: false,
        layerType: 'line',
        sourceType: 'vector',
      },
      metadata: {
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: { coordinates: true, properties: ['EntityHandle'] },
        uniqueBy: 'EntityHandle',
        labelBy: 'EntityHandle',
      },
      searchOptions: {
        interestingProperties: [{ key: 'EntityHandle', type: 'contains' }],
        searchable: false,
      },
      useWorker: true,
    },
    {
      id: 'PUMP_STATION_WASTEWATER',
      name: _('Wastewater pump stations'),
      orderingWeight: 0,
      selectable: true,
      asMarkers: true,
      metadata: {
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          coordinates: true,
          properties: ['id', 'name', 'description', 'type'],
        },
        uniqueBy: 'id',
        labelBy: 'name',
        markerHeight: 46,
        markerWidth: 46,
        shrinkOnZoom: true,
        vivoLinkable: true,
      },
      searchOptions: {
        interestingProperties: [
          { key: 'id', type: 'contains' },
          { key: 'name', type: 'contains' },
          { key: 'description', type: 'contains' },
          { key: 'type', type: 'contains' },
        ],
        searchable: true,
        minimumSearchTermLength: 3,
      },
      useWorker: true,
      style: {
        icon: {
          url: environment.assetRoot + '/img/rego/jvpumppaamo.svg',
          fill: '#C4883A',
        },
        label: {
          color: '#363636',
          fontFamily: 'Lato',
          fontSize: '20px',
          fontWeight: 'normal',
          text: 'name',
          visibleFrom: 14,
        },
        visible: false,
        layerType: 'html-marker',
        sourceType: 'geojson'
      },
      geojsonURL:
        environment.assetRoot + '/data/T13/points/PUMP_STATION_WASTEWATER.json',
      filter: {
        filters: [{ field: 'type', value: 'PUMP_STATION_WASTEWATER' }],
      },
    },
    {
      id: 'ANALYZE_POINT_WATER_OBSERVATION_TUBE',
      name: _('Observation tubes'),
      orderingWeight: 0,
      selectable: true,
      asMarkers: true,
      metadata: {
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          coordinates: true,
          properties: ['ele', 'name', 'desc', 'time'],
        },
        uniqueBy: 'name',
        labelBy: 'name',
        markerHeight: 46,
        markerWidth: 46,
        shrinkOnZoom: true,
        vivoLinkable: true,
      },
      searchOptions: {
        minimumSearchTermLength: 3,
        interestingProperties: [
          { key: 'ele', type: 'contains' },
          { key: 'name', type: 'contains' },
          { key: 'desc', type: 'contains' },
          { key: 'time', type: 'contains' },
        ],
        searchable: true,
      },
      useWorker: true,
      style: {
        icon: {
          url: environment.assetRoot + '/img/rego/analyysipiste.svg',
          fill: getCSSColorVariableValue('teal'),
        },
        label: {
          color: '#363636',
          fontFamily: 'Lato',
          fontSize: '20px',
          fontWeight: 'normal',
          text: 'name',
          visibleFrom: 14,
        },
        visible: false,
        layerType: 'html-marker',
        sourceType: 'geojson'
      },
      geojsonURL:
        environment.assetRoot + '/data/T13/points/ANALYZE_POINT_WATER_OBSERVATION_TUBE.json',
      filter: {
        filters: [
          { field: 'type', value: 'ANALYZE_POINT_WATER_OBSERVATION_TUBE' },
        ],
      },
    },
    ...sharedLayers
  ];
}
